import { useCallback, useMemo, useState } from "react";

import {
  useAllDocumentsSelect,
  useAllFilesSelect,
  useAllFormsSelect,
  useDocumentTypesSelect,
  useFoldersSelect
} from "store";
import { DealFolderForm, IFile, IFolder } from "store/types";
import { Document } from "store/types/documents";
import { ISaveDealAsTemplateData } from "store/types/templates";

import { PickerFilters, usePickerFilters } from "./use-picker-filters";

interface SelectItem {
  id: number;
  parentId: number | null;
}

interface SelectionState {
  folders: SelectItem[];
  files: SelectItem[];
  forms: SelectItem[];
  documents: SelectItem[];
}

type SelectionKey = keyof SelectionState;

const defaultState: SelectionState = { folders: [], files: [], forms: [], documents: [] };

export type TemplateFilters = PickerFilters;

export const useTemplateSelection = () => {
  const [state, setState] = useState<SelectionState>(defaultState);
  const [templateName, setTemplateName] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const {
    filters,
    hasActiveFilters,
    setClassifications,
    setInvestors,
    setEffectiveDate,
    clearFilter,
    clearAllFilters
  } = usePickerFilters();

  const folders = useFoldersSelect({ kind: "folder" });
  const files = useAllFilesSelect();
  const forms = useAllFormsSelect();
  const documents = useAllDocumentsSelect();
  const documentTypes = useDocumentTypesSelect();

  // Use the workspace's canonical Altius Classification order so the filter list
  // matches the file table (instead of an ad-hoc alphabetical order).
  const classificationOptions = useMemo(() => documentTypes, [documentTypes]);

  const investorIdsInFiles = useMemo(() => {
    const ids = new Set<number>();
    files.forEach((f) => {
      if (f.investor_id) ids.add(f.investor_id);
    });
    return Array.from(ids);
  }, [files]);

  const getAllDescendantFolderIds = useCallback(
    (parentId: number): number[] => {
      const children = folders.filter((f) => f.parent_id === parentId);
      const ids: number[] = [];
      children.forEach((child) => {
        ids.push(child.id);
        ids.push(...getAllDescendantFolderIds(child.id));
      });
      return ids;
    },
    [folders]
  );

  const getAncestorFolderIds = useCallback(
    (id: number): SelectItem[] => {
      const result: SelectItem[] = [];
      const folder = folders.find((f) => f.id === id);
      if (folder) {
        result.push({ id: folder.id, parentId: folder.parent_id || null });
        if (folder.parent_id) {
          result.push(...getAncestorFolderIds(folder.parent_id));
        }
      }
      return result;
    },
    [folders]
  );

  // Membership-based (not length-based) so repeated select/unselect stays consistent
  // even when manual toggles add a different mix than "select all".
  const isSelectedAll: boolean = useMemo(() => {
    const nonPrivateFolders = folders.filter((f) => !f.is_private);
    const nonPrivateFiles = files.filter((f) => !f.is_private);
    const nonPrivateForms = forms.filter((f) => !f.is_private);
    const nonPrivateDocuments = documents.filter((d) => !d.is_private);

    const totalSelectable =
      nonPrivateFolders.length + nonPrivateFiles.length + nonPrivateForms.length + nonPrivateDocuments.length;
    if (totalSelectable === 0) return false;

    const folderIds = new Set(state.folders.map((f) => f.id));
    const fileIds = new Set(state.files.map((f) => f.id));
    const formIds = new Set(state.forms.map((f) => f.id));
    const documentIds = new Set(state.documents.map((d) => d.id));

    return (
      nonPrivateFolders.every((f) => folderIds.has(f.id)) &&
      nonPrivateFiles.every((f) => fileIds.has(f.id)) &&
      nonPrivateForms.every((f) => formIds.has(f.id)) &&
      nonPrivateDocuments.every((d) => documentIds.has(d.id))
    );
  }, [state, folders, files, forms, documents]);

  const isSomeSelected: boolean = useMemo(
    () => state.folders.length + state.files.length + state.forms.length + state.documents.length > 0,
    [state]
  );

  const matchesSearch = useCallback(
    (name: string): boolean => {
      if (!searchQuery) return true;
      return name.toLowerCase().includes(searchQuery.toLowerCase());
    },
    [searchQuery]
  );

  const matchesFilters = useCallback(
    (file: IFile): boolean => {
      if (filters.classifications.length) {
        if (!file.document_type || !filters.classifications.includes(file.document_type)) return false;
      }
      if (filters.investors.length) {
        if (!file.investor_id || !filters.investors.includes(file.investor_id)) return false;
      }
      if (filters.effectiveDate) {
        if (!file.date || !file.date.startsWith(filters.effectiveDate)) return false;
      }
      return true;
    },
    [filters]
  );

  const toggleFolder = useCallback(
    (id: number) => {
      const isCurrentlySelected = state.folders.some((f) => f.id === id);

      if (isCurrentlySelected) {
        const descendantIds = getAllDescendantFolderIds(id);
        const idsToRemove = new Set([id, ...descendantIds]);

        setState({
          folders: state.folders.filter((f) => !idsToRemove.has(f.id)),
          files: state.files.filter((f) => f.parentId === null || !idsToRemove.has(f.parentId)),
          forms: state.forms.filter((f) => f.parentId === null || !idsToRemove.has(f.parentId)),
          documents: state.documents.filter((d) => d.parentId === null || !idsToRemove.has(d.parentId))
        });
      } else {
        const isFiltering = !!searchQuery || hasActiveFilters;
        const descendantIds = getAllDescendantFolderIds(id);
        const allFolderIds = [id, ...descendantIds];

        /* Collect visible items first so we know which folders actually have content */
        const newFiles = [...state.files];
        const newForms = [...state.forms];
        const newDocuments = [...state.documents];
        const foldersWithContent = new Set<number>();

        allFolderIds.forEach((folderId) => {
          files
            .filter((f) => f.section_id === folderId && matchesSearch(f.name) && matchesFilters(f))
            .forEach((f) => {
              if (!newFiles.some((sf) => sf.id === f.id)) {
                newFiles.push({ id: f.id, parentId: folderId });
              }
              foldersWithContent.add(folderId);
            });

          if (!hasActiveFilters) {
            forms
              .filter((f) => f.section_id === folderId && matchesSearch(f.title))
              .forEach((f) => {
                if (!newForms.some((sf) => sf.id === f.id)) {
                  newForms.push({ id: f.id, parentId: folderId });
                }
                foldersWithContent.add(folderId);
              });

            documents
              .filter((d) => d.section_id === folderId && matchesSearch(d.title))
              .forEach((d) => {
                if (!newDocuments.some((sd) => sd.id === d.id)) {
                  newDocuments.push({ id: d.id, parentId: folderId });
                }
                foldersWithContent.add(folderId);
              });
          }
        });

        /* When filtering, only add folders that have matching content (or are ancestors of such) */
        const newFolders = [...state.folders];

        if (isFiltering) {
          /* Walk up from each folder with content to mark all ancestors as needed */
          const neededFolderIds = new Set<number>();
          foldersWithContent.forEach((fId) => {
            const ancestorItems = getAncestorFolderIds(fId);
            ancestorItems.forEach((a) => neededFolderIds.add(a.id));
          });

          allFolderIds.forEach((folderId) => {
            if (!neededFolderIds.has(folderId)) return;
            const folder = folders.find((f) => f.id === folderId);
            if (folder && !newFolders.some((f) => f.id === folderId)) {
              newFolders.push({ id: folderId, parentId: folder.parent_id || null });
            }
          });
        } else {
          allFolderIds.forEach((folderId) => {
            const folder = folders.find((f) => f.id === folderId);
            if (folder && !newFolders.some((f) => f.id === folderId)) {
              newFolders.push({ id: folderId, parentId: folder.parent_id || null });
            }
          });
        }

        /* Add ancestors of the toggled folder itself */
        const ancestors = getAncestorFolderIds(id);
        ancestors.forEach((ancestor) => {
          if (!newFolders.some((f) => f.id === ancestor.id)) {
            newFolders.push(ancestor);
          }
        });

        setState({ folders: newFolders, files: newFiles, forms: newForms, documents: newDocuments });
      }
    },
    [state, folders, files, forms, documents, getAllDescendantFolderIds, getAncestorFolderIds, matchesSearch, matchesFilters, hasActiveFilters, searchQuery]
  );

  const toggleItem = useCallback(
    (key: Exclude<SelectionKey, "folders">, item: SelectItem) => {
      const isCurrentlySelected = state[key].some((el) => el.id === item.id);
      const newState = { ...state };

      if (isCurrentlySelected) {
        newState[key] = newState[key].filter((el) => el.id !== item.id);
      } else {
        newState[key] = [...newState[key], item];

        if (item.parentId) {
          const ancestors = getAncestorFolderIds(item.parentId);
          ancestors.forEach((ancestor) => {
            if (!newState.folders.some((f) => f.id === ancestor.id)) {
              newState.folders = [...newState.folders, ancestor];
            }
          });
        }

        if (key === "files") {
          const selectedFile = files.find((f) => f.id === item.id);
          if (selectedFile?.form_id) {
            const form = forms.find((f) => f.id === selectedFile.form_id);
            if (form && !newState.forms.some((el) => el.id === form.id)) {
              newState.forms = [...newState.forms, { id: form.id, parentId: form.section_id }];
              if (form.section_id && !newState.folders.some((f) => f.id === form.section_id)) {
                const formAncestors = getAncestorFolderIds(form.section_id);
                formAncestors.forEach((ancestor) => {
                  if (!newState.folders.some((f) => f.id === ancestor.id)) {
                    newState.folders = [...newState.folders, ancestor];
                  }
                });
              }
            }
          }
        }
      }

      setState(newState);
    },
    [state, files, forms, getAncestorFolderIds]
  );

  const toggleAll = useCallback(() => {
    if (isSelectedAll) {
      setState(defaultState);
      return;
    }

    const folderItems: SelectItem[] = folders
      .filter((f) => !f.is_private)
      .map((f) => ({ id: f.id, parentId: f.parent_id || null }));

    const fileItems: SelectItem[] = files
      .filter((f) => !f.is_private)
      .map((f) => ({ id: f.id, parentId: f.section_id }));

    const formItems: SelectItem[] = forms
      .filter((f) => !f.is_private)
      .map((f) => ({ id: f.id, parentId: f.section_id }));

    const documentItems: SelectItem[] = documents
      .filter((d) => !d.is_private)
      .map((d) => ({ id: d.id, parentId: d.section_id ?? null }));

    setState({ folders: folderItems, files: fileItems, forms: formItems, documents: documentItems });
  }, [folders, files, forms, documents, isSelectedAll]);

  const isSelected = useCallback(
    (key: SelectionKey, id: number): boolean => {
      return state[key].some((item) => item.id === id);
    },
    [state]
  );

  const buildPayload = useCallback((): ISaveDealAsTemplateData["sections"] => {
    return state.folders.reduce(
      (acc, folder) => ({
        ...acc,
        [folder.id]: {
          parent_id: folder.parentId,
          documents: state.documents.filter((d) => d.parentId === folder.id).map((d) => d.id),
          forms: state.forms.filter((f) => f.parentId === folder.id).map((f) => f.id),
          files: state.files.filter((f) => f.parentId === folder.id).map((f) => f.id)
        }
      }),
      {} as ISaveDealAsTemplateData["sections"]
    );
  }, [state]);

  const selectedCount = useMemo(
    () => state.folders.length + state.files.length + state.forms.length + state.documents.length,
    [state]
  );

  const parentFolders = useMemo(() => folders.filter((f) => !f.parent_id), [folders]);

  const filteredFileCount = useMemo(
    () => files.filter((f) => matchesSearch(f.name) && matchesFilters(f)).length,
    [files, matchesSearch, matchesFilters]
  );

  const folderHasMatchingContent = useCallback(
    (folderId: number): boolean => {
      if (!searchQuery && !hasActiveFilters) return true;

      const folderFiles = files.filter((f) => f.section_id === folderId);
      const folderForms = forms.filter((f) => f.section_id === folderId);
      const folderDocs = documents.filter((d) => d.section_id === folderId);

      const hasDirectMatch =
        folderFiles.some((f) => matchesSearch(f.name) && matchesFilters(f)) ||
        (!hasActiveFilters && folderForms.some((f) => matchesSearch(f.title))) ||
        (!hasActiveFilters && folderDocs.some((d) => matchesSearch(d.title)));

      if (hasDirectMatch) return true;

      const childFolders = folders.filter((f) => f.parent_id === folderId);
      return childFolders.some((f) => (!hasActiveFilters && matchesSearch(f.title)) || folderHasMatchingContent(f.id));
    },
    [searchQuery, hasActiveFilters, files, forms, documents, folders, matchesSearch, matchesFilters]
  );

  const removeFolder = useCallback(
    (id: number) => {
      const descendantIds = getAllDescendantFolderIds(id);
      const idsToRemove = new Set([id, ...descendantIds]);

      setState((prev) => ({
        folders: prev.folders.filter((f) => !idsToRemove.has(f.id)),
        files: prev.files.filter((f) => f.parentId === null || !idsToRemove.has(f.parentId)),
        forms: prev.forms.filter((f) => f.parentId === null || !idsToRemove.has(f.parentId)),
        documents: prev.documents.filter((d) => d.parentId === null || !idsToRemove.has(d.parentId))
      }));
    },
    [getAllDescendantFolderIds]
  );

  const removeItem = useCallback((key: Exclude<SelectionKey, "folders">, id: number) => {
    setState((prev) => ({
      ...prev,
      [key]: prev[key].filter((item) => item.id !== id)
    }));
  }, []);

  const clearSelection = useCallback(() => {
    setState(defaultState);
  }, []);

  return {
    state,
    isSelectedAll,
    isSomeSelected,
    toggleFolder,
    toggleItem,
    toggleAll,
    isSelected,
    buildPayload,
    folders,
    files,
    forms,
    documents,
    parentFolders,
    templateName,
    setTemplateName,
    selectedCount,
    searchQuery,
    setSearchQuery,
    matchesSearch,
    matchesFilters,
    folderHasMatchingContent,
    removeFolder,
    removeItem,
    clearSelection,
    filters,
    classificationOptions,
    investorIdsInFiles,
    setClassifications,
    setInvestors,
    setEffectiveDate,
    clearFilter,
    clearAllFilters,
    filteredFileCount,
    hasActiveFilters
  };
};

export type TemplateSelectionReturn = ReturnType<typeof useTemplateSelection>;
export type { SelectItem, SelectionKey, IFolder, IFile, DealFolderForm, Document };
